import { h } from '../dom';
import type { ScreenCtx, ReportParams } from './context';
import { categoryBars, statBlock, type ScreenInstance } from './common';
import { LETTER_COLORS, formatCompact, TIME_LABELS, WEATHER_LABELS } from '../format';
import { buildShareText } from '../../meta/share';
import { goalCoverage, recordMatch, type RecordOutcome } from '../../meta/progress';
import { unlockDef } from '../../meta/unlocks';
import { createSparkline } from '../widgets';
import { prefersReducedMotion } from '../dom';

async function copyText(text: string): Promise<boolean> {
  try {
    const timeout = new Promise<never>((_, rej) => setTimeout(() => rej(new Error('clipboard timeout')), 800));
    await Promise.race([navigator.clipboard.writeText(text), timeout]);
    return true;
  } catch { /* permission denied / insecure context / hung prompt: fall through */ }
  try {
    const ta = h('textarea', { style: 'position:fixed;opacity:0;left:-9999px' });
    ta.value = text; document.body.append(ta); ta.select();
    const ok = document.execCommand('copy'); ta.remove(); return ok;
  } catch { return false; }
}

export function reportScreen(ctx: ScreenCtx, p: ReportParams): ScreenInstance {
  const { summary, extras = {} } = p;
  const r = summary.report;
  const last = ctx.lastStart();
  const opts = last?.options;

  // Persist once per screen instance.
  let outcome: RecordOutcome = { isNewDailyBest: false, granted: [] };
  if (opts) ctx.store.update((d) => { outcome = recordMatch(d, summary, opts, extras); });

  const peak = Math.max(extras.peakViewers ?? 0, r.viewers);
  const bestStreak = Math.max(extras.bestStreak ?? 0, r.streak);
  const gc = goalCoverage(summary);
  const color = LETTER_COLORS[r.letter];

  // --- grade reveal
  const ratingNum = h('span', { class: 'sd-rep__ratingnum', text: '0' });
  const letter = h('div', { class: 'sd-rep__letter', text: r.letter });
  const reveal = h('div', { class: 'sd-rep__reveal', style: `--lc:${color}` }, [
    h('div', { class: 'sd-rep__rays' }),
    h('div', { class: 'sd-rep__ring' }),
    letter,
    h('div', { class: 'sd-rep__sparks' }, Array.from({ length: 14 }, (_, i) => h('i', { style: `--a:${(i * 360) / 14}deg; --d:${0.6 + (i % 4) * 0.2}` }))),
  ]);

  // --- banner (career / daily outcome)
  let banner: HTMLElement | null = null;
  if (outcome.career) {
    const c = outcome.career;
    const text = c.outcome === 'promoted' ? `PROMOTED TO ${c.nextTierName?.toUpperCase()}`
      : c.outcome === 'champion' ? 'CUP FINAL DIRECTOR — CHAMPION'
        : c.outcome === 'retry_tier' ? `TIER AVERAGE ${Math.round(c.average)} — NEEDED B (70). REPLAY ${c.tierName.toUpperCase()}`
          : `${c.tierName.toUpperCase()} · TIER AVERAGE ${Math.round(c.average)}`;
    banner = h('div', { class: `sd-rep__banner is-${c.outcome}` }, [h('span', { text })]);
  } else if (opts?.mode === 'daily') {
    banner = h('div', { class: `sd-rep__banner ${outcome.isNewDailyBest ? 'is-promoted' : ''}` }, [h('span', { text: outcome.isNewDailyBest ? `NEW DAILY BEST · #${opts.daily?.number ?? ''}` : `DAILY #${opts.daily?.number ?? ''} · BEST STANDS` })]);
  }
  const unlocks = outcome.granted.length
    ? h('div', { class: 'sd-rep__unlocks' }, outcome.granted.map((id, i) => {
      const u = unlockDef(id)!;
      return h('div', { class: 'sd-unlockcard', style: `animation-delay:${2.2 + i * 0.25}s` }, [
        h('span', { class: 'sd-unlockcard__k', text: 'UNLOCKED' }), h('b', { text: u.name.toUpperCase() }), h('span', { text: u.description }),
      ]);
    }))
    : null;

  // --- key moments
  const moments = h('ul', { class: 'sd-moments' }, r.keyMoments.length
    ? r.keyMoments.map((k, i) => h('li', { class: `sd-moment ${k.covered ? 'is-covered' : 'is-missed'}`, style: `animation-delay:${1.4 + i * 0.06}s` }, [
      h('span', { class: 'sd-moment__icon', text: k.covered ? '✓' : '✗' }),
      h('span', { class: 'sd-moment__label', text: k.label }),
      k.replayed ? h('span', { class: 'sd-moment__rp', attrs: { title: 'Replayed' }, text: '↺' }) : h('span', { class: 'sd-moment__rp is-off', text: '↺' }),
    ]))
    : [h('li', { class: 'sd-moment is-empty', text: 'A quiet one. No key moments.' })]);

  const spark = createSparkline(200, 40, 'sd-rep__spark');
  if (extras.viewerHistory && extras.viewerHistory.length > 1) spark.set(extras.viewerHistory);

  // --- buttons
  const shareBtn = h('button', { class: 'sd-btn', attrs: { type: 'button' } }, [h('span', { text: 'SHARE RESULT' })]);
  shareBtn.addEventListener('click', async () => {
    const text = buildShareText(summary, {
      dailyNumber: opts?.mode === 'daily' ? opts.daily?.number : undefined,
      modeLabel: opts?.mode === 'career' ? `Career · ${summary.config.competition}` : `Quick Match · Seed ${summary.seed}`,
      url: ctx.deps.shareUrl,
    }, extras);
    const ok = await copyText(text);
    shareBtn.firstChild!.textContent = ok ? 'COPIED ✓' : 'COPY FAILED';
    shareBtn.classList.toggle('is-done', ok);
    ctx.notify(ok ? 'RESULT COPIED TO CLIPBOARD' : 'COULD NOT ACCESS CLIPBOARD');
    setTimeout(() => { shareBtn.firstChild!.textContent = 'SHARE RESULT'; shareBtn.classList.remove('is-done'); }, 2200);
  });
  const next = () => {
    if (!opts) return ctx.show('title');
    if (opts.mode === 'career') return ctx.startCareer();
    if (opts.mode === 'daily') return ctx.show('quick');
    ctx.startQuick((Math.random() * 0xffffffff) >>> 0, opts.difficulty, opts.halfLengthSeconds);
  };
  const hl = ctx.deps.highlights;
  const hasReel = !!hl?.available();
  const watchBtn = hasReel
    ? h('button', { class: 'sd-btn sd-btn--primary', attrs: { type: 'button', 'data-action': 'watch-highlights' }, on: { click: () => hl!.watch() } }, [h('span', { text: '▶ WATCH HIGHLIGHTS' }), h('kbd', { text: 'H' })])
    : null;
  const saveBtn = hasReel && hl!.canExport()
    ? h('button', { class: 'sd-btn', attrs: { type: 'button', 'data-action': 'save-video', title: 'Record your highlights reel to a video file' }, on: { click: () => hl!.save() } }, [h('span', { text: 'SAVE VIDEO' })])
    : null;
  const nextLabel = opts?.mode === 'daily' ? 'QUICK MATCH' : opts?.mode === 'career' && outcome.career?.outcome === 'champion' ? 'REPLAY FINAL' : 'NEXT MATCH';

  const el = h('section', { class: 'sd-screen sd-report', style: `--lc:${color}` }, [
    h('div', { class: 'sd-report__bg' }),
    h('div', { class: 'sd-report__inner' }, [
      h('header', { class: 'sd-rep__head' }, [
        h('div', { class: 'sd-rep__kicker', text: `FULL-TIME · ${summary.config.competition.toUpperCase()}` }),
        h('div', { class: 'sd-rep__scoreline' }, [
          h('span', { text: summary.homeName.toUpperCase() }),
          h('b', { text: `${summary.score.home} – ${summary.score.away}` }),
          h('span', { text: summary.awayName.toUpperCase() }),
        ]),
        h('div', { class: 'sd-rep__venue', text: `${summary.config.venue.toUpperCase()} · ${WEATHER_LABELS[summary.config.weather]} · ${TIME_LABELS[summary.config.timeOfDay]} · SEED ${summary.seed}` }),
      ]),
      h('div', { class: 'sd-rep__grid' }, [
        h('div', { class: 'sd-rep__gradecol' }, [
          reveal,
          h('div', { class: 'sd-rep__rating' }, [h('span', { class: 'sd-field__label', text: 'DIRECTOR RATING' }), ratingNum]),
          banner,
          unlocks,
        ]),
        h('div', { class: 'sd-rep__mid sd-panel' }, [
          h('div', { class: 'sd-section-head' }, [h('span', { text: 'BREAKDOWN' })]),
          categoryBars(r.byCategory, { stagger: 0.1 }),
          h('div', { class: 'sd-rep__stats' }, [
            statBlock('PEAK VIEWERS', formatCompact(peak)),
            statBlock('BEST STREAK', String(bestStreak)),
            statBlock('TOTAL POINTS', Math.round(r.total).toLocaleString('en-GB')),
            statBlock('GOALS COVERED', `${gc.covered}/${gc.goals}`),
            statBlock('CUTS', String(summary.cuts.length)),
          ]),
          extras.viewerHistory && extras.viewerHistory.length > 1 ? h('div', { class: 'sd-rep__audience' }, [h('span', { class: 'sd-field__label', text: 'AUDIENCE' }), spark.el]) : null,
        ]),
        h('div', { class: 'sd-rep__moments sd-panel' }, [
          h('div', { class: 'sd-section-head' }, [h('span', { text: 'KEY MOMENTS' }), h('span', { class: 'sd-legend', text: '✓ COVERED  ✗ MISSED  ↺ REPLAYED' })]),
          moments,
        ]),
      ]),
      h('footer', { class: 'sd-rep__actions' }, [
        h('button', { class: 'sd-btn sd-btn--ghost', attrs: { type: 'button' }, on: { click: () => ctx.show('title') } }, [h('span', { text: 'MENU' })]),
        h('button', { class: 'sd-btn', attrs: { type: 'button' }, on: { click: () => ctx.retry() } }, [h('span', { text: '↻ RETRY SEED' })]),
        shareBtn,
        saveBtn,
        watchBtn,
        h('button', { class: hasReel ? 'sd-btn' : 'sd-btn sd-btn--primary', attrs: { type: 'button' }, on: { click: next } }, [h('span', { text: nextLabel }), h('kbd', { text: 'ENTER' })]),
      ]),
    ]),
  ]);

  let raf = 0;
  return {
    el,
    mounted() {
      const target = Math.round(r.rating);
      if (prefersReducedMotion()) { ratingNum.textContent = String(target); el.classList.add('is-revealed'); return; }
      const t0 = performance.now(), dur = 1300;
      const tick = (now: number) => {
        const k = Math.max(0, Math.min(1, (now - t0) / dur));
        ratingNum.textContent = String(Math.round(target * (1 - Math.pow(1 - k, 3))));
        if (k < 1) raf = requestAnimationFrame(tick);
        else el.classList.add('is-revealed');
      };
      raf = requestAnimationFrame(tick);
    },
    onKey(e) {
      if (hl?.isOpen()) return false;
      if (hasReel && (e.key === 'h' || e.key === 'H')) { hl!.watch(); return true; }
      if (e.key === 'Enter' && !(document.activeElement instanceof HTMLButtonElement)) { next(); return true; }
      return false;
    },
    destroy() { cancelAnimationFrame(raf); },
  };
}
